"use client";
import { ArrowDown2 } from "iconsax-reactjs";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import {
  Controller,
  type FieldPath,
  type FieldValues,
  useFormContext,
} from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

// ----------------------------------------------------------------------

type SelectOption = {
  label: string;
  value: string;
};

interface RHFInlineSelectProps<TFieldValues extends FieldValues>
  extends Omit<
      ComponentPropsWithoutRef<typeof Controller>,
      "name" | "control" | "render"
    >,
    Omit<ComponentPropsWithoutRef<typeof Select>, "name" | "defaultValue"> {
  name: FieldPath<TFieldValues>;
  options: SelectOption[];
  placeholder?: string;
  triggerClassName?: string;
  contentClassName?: string;
  itemClassName?: string;
  /** Custom icon to replace the default dropdown arrow */
  icon?: ReactNode;
}

/**
 * A compact inline select component designed for use inside input adornments.
 * No border, minimal padding, and compact styling.
 */
export default function RHFInlineSelect<TFieldValues extends FieldValues>({
  name,
  defaultValue,
  rules,
  options,
  placeholder,
  triggerClassName,
  contentClassName,
  itemClassName,
  icon,
  onValueChange,
  ...other
}: RHFInlineSelectProps<TFieldValues>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field }) => {
        return (
          <Select
            onValueChange={(value) => {
              field.onChange(value);
              onValueChange?.(value);
            }}
            value={field.value || ""}
            {...other}
          >
            <SelectTrigger
              className={cn(
                "w-auto flex-row-reverse border-0 shadow-none h-auto px-1 py-0 gap-1 focus-visible:ring-0 bg-transparent text-text-disabled",
                triggerClassName,
              )}
              icon={
                icon ?? (
                  <ArrowDown2
                    variant="Bold"
                    className="text-text-disabled"
                    size={16}
                  />
                )
              }
            >
              <SelectValue placeholder={placeholder} className="mt-0.5" />
            </SelectTrigger>
            <SelectContent
              className={cn(
                "bg-bg-secondary rounded-sm border-stroke-subtle shadow-[0px_2px_4px_-2px_#0000001A,0px_4px_6px_-1px_#0000001A] min-w-20",
                contentClassName,
              )}
            >
              {options.map((option, idx) => (
                <SelectItem
                  key={`${option.value}-${idx}`}
                  value={option.value}
                  className={cn(
                    "font-medium rounded-sm text-sm",
                    itemClassName,
                  )}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }}
    />
  );
}
